import {
  SiteFeaturesQuery,
  SiteFeaturesQueryVariables,
} from '__generated__/graphql/legacy/graphql';
import { features } from 'constants/featureFlags';

import React, { ReactNode, createContext, useContext } from 'react';

import { useQuery } from '@apollo/client';

import { useCommunityContext } from 'components/CommunityContext';

import { SITE_FEATURES_QUERY } from 'schema/SiteFeatures/queries';

interface SiteFeaturesContextProviderProps {
  children: ReactNode;
}

export interface SiteFeatures {
  siteFeatures?: {
    communities?: {
      maxCommunitySpaces: number;
    } | null;
    communityEvents?: {
      isEnabled?: boolean | null;
    } | null;
    communityEventNotifications?: {
      isEnabled?: boolean | null;
    } | null;
    directMessaging?: {
      isEnabled: boolean | null;
    } | null;
  } | null;
  communitySpacesTotalCount?: number | null;
  exceededPlanSpaces?: boolean;
  directMessagingEnabled: boolean | undefined;
  isDMsAccessible: boolean;
}

const SiteFeaturesContext = createContext({} as SiteFeatures);

const useSiteFeaturesContext = (): SiteFeatures => useContext(SiteFeaturesContext);

const SiteFeaturesProvider: React.FC<SiteFeaturesContextProviderProps> = ({ children }) => {
  const {
    community: { id },
    featureFlags,
    site,
  } = useCommunityContext();

  const { data } = useQuery<SiteFeaturesQuery, SiteFeaturesQueryVariables>(SITE_FEATURES_QUERY, {
    variables: { communityId: id },
  });

  const siteFeatures = data?.site?.features;
  const maxSpacesAllowed = siteFeatures?.communities?.maxCommunitySpaces;
  const communitySectionsEdges = data?.community?.communitySections?.edges;
  // @ts-ignore ignore until schema is updated
  const directMessagingInPlan = data?.site?.features?.directMessaging?.isEnabled;
  const communitySpacesTotalCount =
    communitySectionsEdges && communitySectionsEdges[0]?.node?.communitySpaces?.totalCount;
  const exceededPlanSpaces =
    maxSpacesAllowed && communitySpacesTotalCount
      ? maxSpacesAllowed - communitySpacesTotalCount <= 0
      : false;

  const isDMsAccessible =
    featureFlags?.[features.DIRECT_MESSAGING] &&
    directMessagingInPlan &&
    !!site?.directMessagingEnabled;

  return (
    <SiteFeaturesContext.Provider
      value={{
        siteFeatures,
        communitySpacesTotalCount,
        exceededPlanSpaces,
        directMessagingEnabled: site?.directMessagingEnabled,
        isDMsAccessible,
      }}
    >
      {children}
    </SiteFeaturesContext.Provider>
  );
};

export { SiteFeaturesContext, SiteFeaturesProvider, useSiteFeaturesContext };
